import { useEffect, useState } from "react";
import client, { authApi, LOGO_MARK } from "../lib/api";
import { toast } from "sonner";

const TABS = [
  { key: "events", icon: "fa-calendar-star", roles: ["founder", "admin"] },
  { key: "promos", icon: "fa-photo-film", roles: ["founder", "admin"] },
  { key: "tickets", icon: "fa-ticket", roles: ["founder", "admin"] },
  { key: "donations", icon: "fa-hand-holding-heart", roles: ["founder", "admin"] },
  { key: "invoices", icon: "fa-file-invoice", roles: ["founder", "admin"] },
  { key: "campaigns", icon: "fa-bullhorn", roles: ["founder", "admin"] },
  { key: "music", icon: "fa-music", roles: ["founder", "admin"] },
  { key: "live", icon: "fa-signal-stream", roles: ["founder", "admin"] },
  { key: "content", icon: "fa-file-lines", roles: ["founder", "admin"] },
  { key: "analytics", icon: "fa-chart-line", roles: ["founder", "admin"] },
  { key: "finance", icon: "fa-vault", roles: ["founder"] },
  { key: "users", icon: "fa-users-gear", roles: ["founder"] },
  { key: "password", icon: "fa-key", roles: ["founder", "admin"] },
];

export default function AdminDashboard({ t, user, onLogout }) {
  const [tab, setTab] = useState("events");
  const visibleTabs = TABS.filter((x) => x.roles.includes(user.role));

  return (
    <div className="admin-shell amatour-shell">
      <aside className="admin-sidebar">
        <div className="flex items-center gap-3 mb-6">
          <img src={LOGO_MARK} alt="" style={{ height: 42 }} />
          <div>
            <div className="font-display text-lg crown-accent">Amatour</div>
            <div className="text-xs text-zinc-400">{t.admin.title}</div>
          </div>
        </div>
        <div className="card-amatour p-4 mb-5 text-sm">
          <div className="text-xs" style={{ color: "var(--amatour-gold)" }}>{t.admin.welcome}</div>
          <div className="font-semibold text-base mt-1">{user.name}</div>
          <span className={`chip mt-2 ${user.role === "founder" ? "chip-gold" : "chip-green"}`}>
            {user.role === "founder" ? <><i className="fa-solid fa-crown" /> {t.admin.founder}</> : <><i className="fa-solid fa-user-gear" /> {t.admin.collaborator}</>}
          </span>
          {user.role === "founder" && <div className="text-[10px] mt-2 italic-accent">Accès total · Coffre &amp; utilisateurs</div>}
          {user.role === "admin" && <div className="text-[10px] mt-2 italic-accent">Gestion du site · Pas d'accès coffre</div>}
        </div>
        <nav className="flex flex-col gap-1">
          {visibleTabs.map((x) => (
            <button key={x.key} className={`navlink text-left ${tab === x.key ? "active" : ""}`} onClick={() => setTab(x.key)} data-testid={`tab-${x.key}`}>
              <i className={`fa-solid ${x.icon} w-4`} /> &nbsp;{t.admin.tabs[x.key]}
            </button>
          ))}
        </nav>
        <button className="btn-ghost w-full mt-6" onClick={onLogout} data-testid="btn-logout">
          <i className="fa-solid fa-right-from-bracket" /> &nbsp;{t.nav.logout}
        </button>
      </aside>
      <main className="admin-content">
        {tab === "events" && <EventsPanel t={t} />}
        {tab === "promos" && <PromosPanel t={t} />}
        {tab === "tickets" && <TicketsPanel t={t} />}
        {tab === "donations" && <DonationsPanel t={t} />}
        {tab === "invoices" && <InvoicesPanel t={t} />}
        {tab === "campaigns" && <CampaignsPanel t={t} />}
        {tab === "music" && <MusicPanel t={t} />}
        {tab === "live" && <ContentPanel t={t} liveOnly />}
        {tab === "content" && <ContentPanel t={t} />}
        {tab === "analytics" && <AnalyticsPanel t={t} />}
        {tab === "finance" && <FinancePanel t={t} />}
        {tab === "users" && <UsersPanel t={t} currentUser={user} />}
        {tab === "password" && <PasswordPanel t={t} />}
      </main>
    </div>
  );
}

const Section = ({ title, action, children }) => (
  <div>
    <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
      <h2 className="section-title font-display">{title}</h2>
      {action}
    </div>
    {children}
  </div>
);

// ---------- ÉVÉNEMENTS ----------
function EventsPanel({ t }) {
  const [items, setItems] = useState([]);
  const empty = { title: "", description: "", date: "", location: "", country: "", poster_url: "", price_from: 0, capacity: 0, status: "upcoming", published: true };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => client.get("/events?only_published=false").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, price_from: parseFloat(form.price_from) || 0, capacity: parseInt(form.capacity) || 0, date: form.date ? new Date(form.date).toISOString() : new Date().toISOString() };
    try {
      if (editing) await client.put(`/events/${editing}`, payload);
      else await client.post("/events", payload);
      toast.success("Enregistré");
      setForm(empty); setEditing(null); load();
    } catch { toast.error("Erreur"); }
  };
  const edit = (ev) => { setEditing(ev.id); setForm({ ...ev, date: ev.date ? ev.date.slice(0, 16) : "" }); };
  const del = async (id) => { if (window.confirm("Supprimer ?")) { await client.delete(`/events/${id}`); load(); } };

  return (
    <Section title={t.admin.tabs.events}>
      <form onSubmit={save} className="card-amatour p-6 mb-6 grid md:grid-cols-2 gap-4" data-testid="event-form">
        <div className="md:col-span-2"><label className="label-amatour">Titre *</label><input required className="input-amatour" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title" /></div>
        <div><label className="label-amatour">Date *</label><input type="datetime-local" required className="input-amatour" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
        <div><label className="label-amatour">Lieu</label><input className="input-amatour" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
        <div><label className="label-amatour">Pays</label><input className="input-amatour" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
        <div><label className="label-amatour">Prix à partir de (€)</label><input type="number" className="input-amatour" value={form.price_from} onChange={(e) => setForm({ ...form, price_from: e.target.value })} /></div>
        <div><label className="label-amatour">Capacité</label><input type="number" className="input-amatour" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></div>
        <div><label className="label-amatour">Statut</label>
          <select className="input-amatour" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option value="upcoming">À venir</option><option value="live">Live</option><option value="past">Passé</option><option value="cancelled">Annulé</option>
          </select>
        </div>
        <div><label className="label-amatour">URL affiche</label><input className="input-amatour" value={form.poster_url} onChange={(e) => setForm({ ...form, poster_url: e.target.value })} /></div>
        <div className="md:col-span-2"><label className="label-amatour">Description</label><textarea rows={3} className="input-amatour" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
        <div className="md:col-span-2 flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Publier sur le site</label>
          <div className="flex-1" />
          {editing && <button type="button" className="btn-ghost" onClick={() => { setEditing(null); setForm(empty); }}>Annuler</button>}
          <button type="submit" className="btn-gold" data-testid="event-save">{editing ? "Mettre à jour" : "Créer l'événement"}</button>
        </div>
      </form>

      <div className="card-amatour p-2 overflow-x-auto">
        <table className="table-amatour">
          <thead><tr><th>Date</th><th>Titre</th><th>Lieu</th><th>Prix</th><th>Statut</th><th></th></tr></thead>
          <tbody>
            {items.map((ev) => (
              <tr key={ev.id}>
                <td>{ev.date && new Date(ev.date).toLocaleDateString()}</td>
                <td><strong>{ev.title}</strong>{!ev.published && <span className="tag ml-2">Brouillon</span>}</td>
                <td>{ev.location}, {ev.country}</td>
                <td>{ev.price_from > 0 ? `${ev.price_from}€` : "—"}</td>
                <td><span className="chip">{ev.status}</span></td>
                <td className="text-right whitespace-nowrap">
                  <button className="btn-ghost" onClick={() => edit(ev)}><i className="fa-solid fa-pen" /></button>
                  <button className="btn-red ml-2" onClick={() => del(ev.id)}><i className="fa-solid fa-trash" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-zinc-500 py-6">Aucun événement</td></tr>}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ---------- BILLETTERIE / LIENS PAIEMENT ----------
function TicketsPanel({ t }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ label: "", amount: "", kind: "ticket", customer_email: "" });
  const [busy, setBusy] = useState(false);

  const load = () => client.get("/payment-links").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await client.post("/payment-links", { ...form, amount: parseFloat(form.amount) });
      toast.success("Lien créé");
      navigator.clipboard?.writeText(data.url).catch(() => {});
      setForm({ label: "", amount: "", kind: "ticket", customer_email: "" });
      load();
    } catch { toast.error("Erreur"); } finally { setBusy(false); }
  };

  return (
    <Section title={t.admin.tabs.tickets}>
      <form onSubmit={create} className="card-amatour p-6 mb-6 grid md:grid-cols-4 gap-4" data-testid="tickets-form">
        <div className="md:col-span-2"><label className="label-amatour">Libellé *</label><input required className="input-amatour" placeholder="Ex: Billet Festival Amatour 2027" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="ticket-label" /></div>
        <div><label className="label-amatour">Montant € *</label><input required type="number" step="0.01" className="input-amatour" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="ticket-amount" /></div>
        <div><label className="label-amatour">Type</label>
          <select className="input-amatour" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            <option value="ticket">Billet</option><option value="subscription">Abonnement</option><option value="invoice">Facture</option><option value="donation_capture">Don confirmé</option>
          </select>
        </div>
        <div className="md:col-span-3"><label className="label-amatour">Email client (optionnel)</label><input type="email" className="input-amatour" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} /></div>
        <div className="flex items-end"><button className="btn-gold w-full" disabled={busy} data-testid="ticket-create">{busy ? "…" : "Générer le lien Stripe"}</button></div>
      </form>

      <div className="card-amatour p-2 overflow-x-auto">
        <table className="table-amatour">
          <thead><tr><th>Date</th><th>Libellé</th><th>Type</th><th>Montant</th><th>Statut</th><th>Lien</th></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id}>
                <td>{new Date(p.created_at).toLocaleDateString()}</td>
                <td>{p.label}</td>
                <td><span className="chip">{p.kind}</span></td>
                <td>{p.amount}€</td>
                <td><span className={`chip ${p.payment_status === "paid" ? "chip-green" : "chip-red"}`}>{p.payment_status}</span></td>
                <td><button className="btn-ghost" onClick={() => { navigator.clipboard.writeText(p.url); toast.success("Lien copié"); }}><i className="fa-solid fa-copy" /> Copier</button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-zinc-500 py-6">Aucun lien</td></tr>}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ---------- DONS ----------
function DonationsPanel({ t }) {
  const [items, setItems] = useState([]);
  useEffect(() => { client.get("/donations").then((r) => setItems(r.data)); }, []);

  const patch = async (id, status) => {
    await client.patch(`/donations/${id}`, { status });
    setItems((prev) => prev.map((d) => d.id === id ? { ...d, status } : d));
    toast.success("Mis à jour");
  };

  return (
    <Section title={t.admin.tabs.donations}>
      <div className="card-amatour p-2 overflow-x-auto">
        <table className="table-amatour">
          <thead><tr><th>Date</th><th>Entreprise</th><th>Contact</th><th>Montant</th><th>Message</th><th>Statut</th></tr></thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id}>
                <td>{new Date(d.created_at).toLocaleDateString()}</td>
                <td><strong>{d.company_name}</strong></td>
                <td><div>{d.contact_name}</div><div className="text-xs text-zinc-500">{d.email} {d.phone}</div></td>
                <td className="text-amber-300 font-semibold">{d.amount}€</td>
                <td className="max-w-xs text-sm text-zinc-400">{d.message}</td>
                <td>
                  <select className="input-amatour" style={{ padding: "6px 10px", fontSize: 12 }} value={d.status} onChange={(e) => patch(d.id, e.target.value)}>
                    <option value="pending">En attente</option><option value="confirmed">Confirmé</option><option value="rejected">Refusé</option>
                  </select>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-zinc-500 py-6">Aucun don</td></tr>}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ---------- FACTURES / DEVIS ----------
function InvoicesPanel({ t }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ kind: "invoice", client_name: "", client_email: "", client_address: "", tva_rate: 20, notes: "" });
  const [lines, setLines] = useState([{ id: crypto.randomUUID(), label: "", qty: 1, unit_price: 0 }]);

  const load = () => client.get("/invoices").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await client.post("/invoices", { ...form, tva_rate: parseFloat(form.tva_rate) || 0,
        items: lines.map((l) => ({ label: l.label, qty: parseFloat(l.qty) || 0, unit_price: parseFloat(l.unit_price) || 0 })) });
      toast.success("Créé");
      setForm({ kind: "invoice", client_name: "", client_email: "", client_address: "", tva_rate: 20, notes: "" });
      setLines([{ id: crypto.randomUUID(), label: "", qty: 1, unit_price: 0 }]);
      load();
    } catch { toast.error("Erreur"); }
  };
  const totalHT = lines.reduce((s, l) => s + (parseFloat(l.qty) || 0) * (parseFloat(l.unit_price) || 0), 0);

  return (
    <Section title={t.admin.tabs.invoices}>
      <form onSubmit={save} className="card-amatour p-6 mb-6 space-y-4" data-testid="invoice-form">
        <div className="grid md:grid-cols-4 gap-4">
          <div><label className="label-amatour">Type</label>
            <select className="input-amatour" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              <option value="invoice">Facture</option><option value="quote">Devis</option>
            </select>
          </div>
          <div><label className="label-amatour">Client *</label><input required className="input-amatour" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></div>
          <div><label className="label-amatour">Email</label><input className="input-amatour" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} /></div>
          <div><label className="label-amatour">TVA %</label><input type="number" className="input-amatour" value={form.tva_rate} onChange={(e) => setForm({ ...form, tva_rate: e.target.value })} /></div>
          <div className="md:col-span-4"><label className="label-amatour">Adresse client</label><input className="input-amatour" value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} /></div>
        </div>
        <div>
          <label className="label-amatour">Lignes</label>
          {lines.map((l, i) => (
            <div key={l.id} className="grid grid-cols-12 gap-2 mb-2">
              <input className="input-amatour col-span-6" placeholder="Description" value={l.label} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
              <input type="number" className="input-amatour col-span-2" placeholder="Qté" value={l.qty} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} />
              <input type="number" step="0.01" className="input-amatour col-span-3" placeholder="PU €" value={l.unit_price} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, unit_price: e.target.value } : x))} />
              <button type="button" className="btn-ghost col-span-1" onClick={() => setLines(lines.filter((_, j) => j !== i))}><i className="fa-solid fa-xmark" /></button>
            </div>
          ))}
          <button type="button" className="btn-ghost" onClick={() => setLines([...lines, { id: crypto.randomUUID(), label: "", qty: 1, unit_price: 0 }])}><i className="fa-solid fa-plus" /> Ajouter ligne</button>
        </div>
        <div><label className="label-amatour">Notes</label><textarea rows={2} className="input-amatour" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        <div className="flex justify-between items-center">
          <div className="text-sm text-zinc-400">Total HT : <span className="text-amber-300 font-semibold">{totalHT.toFixed(2)}€</span></div>
          <button className="btn-gold" data-testid="invoice-save">Créer</button>
        </div>
      </form>

      <div className="card-amatour p-2 overflow-x-auto">
        <table className="table-amatour">
          <thead><tr><th>Numéro</th><th>Type</th><th>Client</th><th>HT</th><th>TVA</th><th>TTC</th></tr></thead>
          <tbody>
            {items.map((inv) => (
              <tr key={inv.id}>
                <td><strong>{inv.number}</strong></td>
                <td><span className="chip">{inv.kind === "quote" ? "Devis" : "Facture"}</span></td>
                <td>{inv.client_name}</td>
                <td>{inv.ht}€</td>
                <td>{inv.tva}€</td>
                <td className="text-amber-300 font-semibold">{inv.ttc}€</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-zinc-500 py-6">Aucun document</td></tr>}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ---------- CAMPAGNES ----------
function CampaignsPanel({ t }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", prompt: "", channel: "poster" });

  const load = () => client.get("/campaigns").then((r) => setItems(r.data));
  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await client.post("/campaigns", form);
      toast.success("Génération lancée");
      setForm({ title: "", prompt: "", channel: "poster" });
      load();
    } catch { toast.error("Erreur"); }
  };
  const del = async (id) => { await client.delete(`/campaigns/${id}`); load(); };

  return (
    <Section title={t.admin.tabs.campaigns}>
      <form onSubmit={create} className="card-amatour p-6 mb-6 grid md:grid-cols-3 gap-4" data-testid="campaign-form">
        <div><label className="label-amatour">Titre</label><input className="input-amatour" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
        <div><label className="label-amatour">Canal</label>
          <select className="input-amatour" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}>
            <option value="poster">Affiche</option><option value="social">Réseau social</option><option value="banner">Bannière</option>
          </select>
        </div>
        <div className="flex items-end"><button className="btn-gold w-full">Générer</button></div>
        <div className="md:col-span-3"><label className="label-amatour">Brief créatif</label><textarea rows={2} className="input-amatour" required placeholder="Ex: Affiche du festival Amatour 2027 à Brazzaville, ambiance nocturne, rumba congolaise" value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} /></div>
      </form>
      <div className="grid md:grid-cols-3 gap-4">
        {items.map((c) => (
          <div key={c.id} className="card-amatour p-4">
            <div className="flex justify-between items-start mb-2"><h4 className="font-semibold">{c.title}</h4><button className="btn-ghost" onClick={() => del(c.id)}><i className="fa-solid fa-trash" /></button></div>
            <span className="chip mb-3 inline-block">{c.channel}</span>
            {c.status === "generating" && <div className="aspect-square bg-black/30 rounded flex items-center justify-center"><i className="fa-solid fa-spinner spin text-2xl text-amber-300" /></div>}
            {c.status === "failed" && <div className="aspect-square bg-black/30 rounded flex items-center justify-center text-red-400 text-sm p-4 text-center">Échec : {c.error}</div>}
            {c.image_url && <img src={c.image_url} alt="" className="w-full rounded" />}
            <p className="text-xs text-zinc-500 mt-2">{c.prompt}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ---------- MUSIQUE ----------
function MusicPanel({ t }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", artist: "", region: "France", country: "", summary: "", genre: "" });

  const load = () => client.get("/music").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const refresh = async () => { setBusy(true); try { await client.post("/music/refresh"); toast.success("Actus IA rafraîchies"); load(); } catch { toast.error("Erreur"); } finally { setBusy(false); } };
  const add = async (e) => { e.preventDefault(); await client.post("/music", form); setForm({ title: "", artist: "", region: "France", country: "", summary: "", genre: "" }); load(); };
  const del = async (id) => { await client.delete(`/music/${id}`); load(); };

  return (
    <Section title={t.admin.tabs.music} action={<button className="btn-gold" onClick={refresh} disabled={busy} data-testid="music-refresh">{busy ? <i className="fa-solid fa-spinner spin" /> : <i className="fa-solid fa-rotate" />} &nbsp;Régénérer avec IA</button>}>
      <form onSubmit={add} className="card-amatour p-6 mb-6 grid md:grid-cols-3 gap-3">
        <input required className="input-amatour" placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <input className="input-amatour" placeholder="Artiste" value={form.artist} onChange={(e) => setForm({ ...form, artist: e.target.value })} />
        <select className="input-amatour" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}><option>France</option><option>Afrique</option></select>
        <input className="input-amatour" placeholder="Pays" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
        <input className="input-amatour" placeholder="Genre" value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })} />
        <button className="btn-gold">Ajouter manuellement</button>
        <textarea rows={2} className="input-amatour md:col-span-3" placeholder="Résumé court" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
      </form>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((m) => (
          <div key={m.id} className="card-amatour p-4 flex justify-between items-start gap-3">
            <div>
              <div className="flex gap-2 mb-1"><span className={`chip ${m.region === "France" ? "chip-red" : "chip-green"}`}>{m.region}</span>{m.genre && <span className="tag">{m.genre}</span>}{m.generated && <span className="tag">IA</span>}</div>
              <h4 className="font-semibold">{m.title}</h4>
              <div className="text-amber-300 text-sm">{m.artist}</div>
              <p className="text-xs text-zinc-400 mt-1">{m.summary}</p>
            </div>
            <button className="btn-red" onClick={() => del(m.id)}><i className="fa-solid fa-trash" /></button>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ---------- CONTENU / LIVE ----------
function ContentPanel({ t, liveOnly }) {
  const [c, setC] = useState(null);
  useEffect(() => { client.get("/content").then((r) => setC(r.data)); }, []);
  if (!c) return null;
  const save = async () => {
    const { _id, id, updated_at, ...rest } = c;
    await client.put("/content", rest);
    toast.success("Contenu enregistré");
  };
  return (
    <Section title={liveOnly ? t.admin.tabs.live : t.admin.tabs.content} action={<button className="btn-gold" onClick={save} data-testid="content-save"><i className="fa-solid fa-floppy-disk" /> Enregistrer</button>}>
      <div className="card-amatour p-6 space-y-4">
        {!liveOnly && <>
          <div><label className="label-amatour">Titre hero</label><input className="input-amatour" value={c.hero_title} onChange={(e) => setC({ ...c, hero_title: e.target.value })} /></div>
          <div><label className="label-amatour">Sous-titre hero</label><input className="input-amatour" value={c.hero_subtitle} onChange={(e) => setC({ ...c, hero_subtitle: e.target.value })} /></div>
          <div><label className="label-amatour">À propos</label><textarea rows={4} className="input-amatour" value={c.about} onChange={(e) => setC({ ...c, about: e.target.value })} /></div>
          <div><label className="label-amatour">Prochain festival</label><input className="input-amatour" value={c.next_festival} onChange={(e) => setC({ ...c, next_festival: e.target.value })} /></div>
        </>}
        <div><label className="label-amatour">Titre du live</label><input className="input-amatour" value={c.live_title || ""} onChange={(e) => setC({ ...c, live_title: e.target.value })} /></div>
        <div><label className="label-amatour">URL embed du live (YouTube, Twitch...)</label><input className="input-amatour" value={c.live_url || ""} onChange={(e) => setC({ ...c, live_url: e.target.value })} data-testid="live-url" /></div>
        <div className="grid md:grid-cols-2 gap-3">
          {["instagram", "facebook", "youtube", "tiktok"].map((n) => (
            <div key={n}><label className="label-amatour capitalize">{n}</label><input className="input-amatour" value={c.socials?.[n] || ""} onChange={(e) => setC({ ...c, socials: { ...c.socials, [n]: e.target.value } })} /></div>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ---------- ANALYTIQUE ----------
function AnalyticsPanel({ t }) {
  const [s, setS] = useState(null);
  useEffect(() => { client.get("/analytics/summary").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-zinc-400">Chargement…</div>;
  return (
    <Section title={t.admin.tabs.analytics}>
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <Kpi label="Total" value={s.total} icon="fa-eye" />
        <Kpi label="24 dernières heures" value={s.last_24h} icon="fa-clock" />
        <Kpi label="7 derniers jours" value={s.last_7d} icon="fa-calendar-week" />
      </div>
      <div className="card-amatour p-6">
        <h3 className="font-semibold mb-3">Pages les plus consultées</h3>
        <table className="table-amatour">
          <thead><tr><th>Page</th><th>Vues</th></tr></thead>
          <tbody>{s.top_pages.map((p) => <tr key={p.path}><td>{p.path}</td><td>{p.count}</td></tr>)}</tbody>
        </table>
      </div>
    </Section>
  );
}
const Kpi = ({ label, value, icon }) => (
  <div className="card-amatour p-6"><div className="flex items-center gap-3"><i className={`fa-solid ${icon} text-2xl text-amber-300`} /><div><div className="text-3xl font-bold">{value}</div><div className="text-xs text-zinc-400 uppercase tracking-wider">{label}</div></div></div></div>
);

// ---------- FINANCE (fondateurs) ----------
function FinancePanel({ t }) {
  const [f, setF] = useState(null);
  useEffect(() => { client.get("/finance/summary").then((r) => setF(r.data)); }, []);
  if (!f) return null;
  return (
    <Section title={t.admin.tabs.finance}>
      <div className="chip chip-red mb-6"><i className="fa-solid fa-shield-halved" /> Zone confidentielle — fondateurs uniquement</div>
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <Kpi label="Total encaissé Stripe" value={`${f.total_paid_eur.toFixed(2)}€`} icon="fa-euro-sign" />
        <Kpi label="Nombre de paiements" value={f.count_paid} icon="fa-credit-card" />
        <Kpi label="Dons confirmés" value={`${f.donations_confirmed_eur.toFixed(2)}€`} icon="fa-hand-holding-heart" />
      </div>
      <div className="card-amatour p-6 mb-6">
        <h3 className="font-semibold mb-3">Répartition par type</h3>
        {Object.entries(f.by_kind).map(([k, v]) => (
          <div key={k} className="flex justify-between py-2 border-b border-white/5"><span>{k}</span><span className="text-amber-300 font-semibold">{v.toFixed(2)}€</span></div>
        ))}
      </div>
      <div className="card-amatour p-2 overflow-x-auto">
        <table className="table-amatour">
          <thead><tr><th>Date</th><th>Libellé</th><th>Type</th><th>Montant</th><th>Statut</th></tr></thead>
          <tbody>
            {f.recent.map((p) => (<tr key={p.id}><td>{new Date(p.created_at).toLocaleDateString()}</td><td>{p.label}</td><td>{p.kind}</td><td>{p.amount}€</td><td><span className="chip chip-green">{p.payment_status}</span></td></tr>))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ---------- UTILISATEURS (fondateurs) ----------
function UsersPanel({ t, currentUser }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ username: "", password: "", name: "", role: "admin" });

  const load = () => authApi.listUsers().then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => { e.preventDefault(); try { await authApi.createUser(form); toast.success("Créé"); setForm({ username: "", password: "", name: "", role: "admin" }); load(); } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); } };
  const del = async (id) => { if (window.confirm("Supprimer ?")) { try { await authApi.deleteUser(id); load(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } } };

  return (
    <Section title={t.admin.tabs.users}>
      <form onSubmit={create} className="card-amatour p-6 mb-6 grid md:grid-cols-5 gap-3">
        <input required className="input-amatour" placeholder="Identifiant" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
        <input required className="input-amatour" placeholder="Mot de passe" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        <input required className="input-amatour md:col-span-2" placeholder="Nom complet" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <select className="input-amatour" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}><option value="admin">Collaborateur</option><option value="founder">Fondateur</option></select>
        <button className="btn-gold md:col-span-5">Créer l'utilisateur</button>
      </form>
      <div className="card-amatour p-2 overflow-x-auto">
        <table className="table-amatour">
          <thead><tr><th>Identifiant</th><th>Nom</th><th>Rôle</th><th></th></tr></thead>
          <tbody>
            {users.map((u) => (<tr key={u.id}>
              <td><code>{u.username}</code></td><td>{u.name}</td>
              <td><span className={`chip ${u.role === "founder" ? "" : "chip-green"}`}>{u.role}</span></td>
              <td className="text-right">{u.id !== currentUser.id && <button className="btn-red" onClick={() => del(u.id)}><i className="fa-solid fa-trash" /></button>}</td>
            </tr>))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ---------- MOT DE PASSE ----------
function PasswordPanel({ t }) {
  const [form, setForm] = useState({ old_password: "", new_password: "", confirm: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) { toast.error("Confirmation différente"); return; }
    try { await authApi.changePassword(form.old_password, form.new_password); toast.success("Mot de passe modifié"); setForm({ old_password: "", new_password: "", confirm: "" }); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <Section title={t.admin.tabs.password}>
      <form onSubmit={submit} className="card-amatour p-6 max-w-md space-y-4">
        <div><label className="label-amatour">Ancien mot de passe</label><input type="password" required className="input-amatour" value={form.old_password} onChange={(e) => setForm({ ...form, old_password: e.target.value })} /></div>
        <div><label className="label-amatour">Nouveau mot de passe</label><input type="password" required className="input-amatour" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} /></div>
        <div><label className="label-amatour">Confirmer</label><input type="password" required className="input-amatour" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} /></div>
        <button className="btn-gold w-full" data-testid="password-submit">Changer le mot de passe</button>
      </form>
    </Section>
  );
}

// ---------- PROMOS : Affiches & Spots pub ----------
function PromosPanel({ t }) {
  const [items, setItems] = useState([]);
  const empty = { kind: "poster", title: "", image_url: "", video_url: "", link: "", position: "events", enabled: true, order: 0 };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => client.get("/promos?only_enabled=false").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const uploadFile = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await client.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const backend = process.env.REACT_APP_BACKEND_URL;
      setForm((prev) => ({ ...prev, [field]: `${backend}${data.url}` }));
      toast.success("Fichier uploadé");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur upload"); }
    finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await client.put(`/promos/${editing}`, form);
      else await client.post("/promos", form);
      toast.success("Enregistré");
      setForm(empty); setEditing(null); load();
    } catch { toast.error("Erreur"); }
  };
  const edit = (p) => { setEditing(p.id); setForm({ ...p }); };
  const del = async (id) => { if (window.confirm("Supprimer cette promo ?")) { await client.delete(`/promos/${id}`); load(); } };
  const toggle = async (p) => { await client.put(`/promos/${p.id}`, { ...p, enabled: !p.enabled }); load(); };

  return (
    <Section title="Affiches & Spots publicitaires">
      <div className="chip mb-6"><i className="fa-solid fa-circle-info" /> Positionnez vos affiches et spots vidéo sur le site public (section Événements) ou en repli du Live.</div>

      <form onSubmit={save} className="card-amatour card-corners p-6 mb-6 grid md:grid-cols-2 gap-4" data-testid="promo-form">
        <div><label className="label-amatour">Type de promo</label>
          <select className="input-amatour" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="promo-kind">
            <option value="poster">Affiche (image)</option>
            <option value="video_ad">Spot vidéo (avec bouton volume)</option>
            <option value="live_fallback">Vidéo de repli LIVE (file d'attente)</option>
          </select>
        </div>
        <div><label className="label-amatour">Emplacement</label>
          <select className="input-amatour" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })}>
            <option value="events">Section Événements</option>
            <option value="live_fallback">Écran Live (repli)</option>
          </select>
        </div>
        <div className="md:col-span-2"><label className="label-amatour">Titre</label><input className="input-amatour" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Affiche Festival 2027" /></div>

        {form.kind === "poster" && (
          <div className="md:col-span-2">
            <label className="label-amatour">Image de l'affiche</label>
            <div className="flex gap-2">
              <input className="input-amatour flex-1" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="URL ou uploadez ci-contre" />
              <label className="btn-outline cursor-pointer" style={{ margin: 0 }}>
                <i className="fa-solid fa-upload" /> &nbsp;Upload
                <input type="file" accept="image/*" onChange={(e) => uploadFile(e, "image_url")} style={{ display: "none" }} disabled={uploading} data-testid="promo-upload-image" />
              </label>
            </div>
            {form.image_url && <img src={form.image_url} alt="preview" className="mt-3 max-h-40 rounded" />}
          </div>
        )}

        {(form.kind === "video_ad" || form.kind === "live_fallback") && (
          <div className="md:col-span-2">
            <label className="label-amatour">URL vidéo (MP4 direct ou embed YouTube/Vimeo)</label>
            <div className="flex gap-2">
              <input className="input-amatour flex-1" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://... ou https://www.youtube.com/embed/..." />
              <label className="btn-outline cursor-pointer" style={{ margin: 0 }}>
                <i className="fa-solid fa-upload" /> &nbsp;Upload MP4
                <input type="file" accept="video/mp4,video/webm" onChange={(e) => uploadFile(e, "video_url")} style={{ display: "none" }} disabled={uploading} />
              </label>
            </div>
            <p className="text-xs italic-accent mt-2 opacity-70">Pour YouTube utilisez l'URL /embed/… (ex: https://www.youtube.com/embed/VIDEO_ID). Pour un MP4 direct, l'affichage aura un bouton volume.</p>
          </div>
        )}

        <div><label className="label-amatour">Lien cliquable (optionnel)</label><input className="input-amatour" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://…" /></div>
        <div><label className="label-amatour">Ordre d'affichage</label><input type="number" className="input-amatour" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} /></div>

        <div className="md:col-span-2 flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm italic-accent"><input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} /> Activer sur le site</label>
          <div className="flex-1" />
          {editing && <button type="button" className="btn-ghost" onClick={() => { setEditing(null); setForm(empty); }}>Annuler</button>}
          <button type="submit" className="btn-gold" data-testid="promo-save">{editing ? "Mettre à jour" : "Ajouter la promo"}</button>
        </div>
      </form>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} className="card-amatour card-corners p-4">
            <div className="flex justify-between items-start mb-3 gap-2">
              <div>
                <div className="flex gap-2 mb-1 flex-wrap">
                  <span className="chip">{p.kind}</span>
                  <span className={`chip ${p.enabled ? "chip-green" : "chip-red"}`}>{p.enabled ? "actif" : "inactif"}</span>
                  <span className="tag">{p.position}</span>
                </div>
                <h4 className="font-display text-base">{p.title || "(sans titre)"}</h4>
              </div>
            </div>
            {p.kind === "poster" && p.image_url && <img src={p.image_url} alt="" className="w-full h-40 object-cover rounded mb-3" />}
            {(p.kind === "video_ad" || p.kind === "live_fallback") && p.video_url && (
              p.video_url.includes("/embed/") || p.video_url.includes("youtube") ?
                <div className="aspect-video mb-3 bg-black/40 rounded flex items-center justify-center italic-accent text-xs opacity-70">Embed YouTube</div> :
                <video src={p.video_url} className="w-full max-h-40 rounded mb-3" muted controls />
            )}
            <div className="flex gap-2 justify-end">
              <button className="btn-ghost" onClick={() => toggle(p)} title={p.enabled ? "Désactiver" : "Activer"}><i className={`fa-solid ${p.enabled ? "fa-eye-slash" : "fa-eye"}`} /></button>
              <button className="btn-ghost" onClick={() => edit(p)}><i className="fa-solid fa-pen" /></button>
              <button className="btn-red" onClick={() => del(p.id)}><i className="fa-solid fa-trash" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full card-amatour p-8 text-center italic-accent opacity-70">Aucune promo</div>}
      </div>
    </Section>
  );
}

